/**
 * Loan Field Catalog — the single source of truth for the drag-and-drop
 * workflow builder palette, the operator picker on the condition row, and
 * the value-control selection (number input vs Y/N toggle vs date picker
 * vs free-text).
 *
 * Design notes (from the workflows-redesign-final.md spec + a prior
 * verification pass against Docs/uwm-insights-guide.txt):
 *
 *  - Every descriptor's `allowedOps` is a strict subset of the operators
 *    that `ConditionSpec` supports. We deliberately do NOT expose
 *    `contains` / `starts_with` / `in` for Phase 1 — those would round-trip
 *    to an unsupported `ConditionSpec` op and fail Zod parse on save.
 *
 *  - Each descriptor declares its `source`:
 *      uwm     — directly read from a UWM Insights field (1:1)
 *      derived — composed by the connector from multiple UWM fields
 *                (e.g. property address is `SubjectProperty.Address1 + City + …`)
 *      manual  — entered by the MLO on the Pipeline drawer; NOT in UWM
 *
 *  - `path` is the dot-path the runtime evaluator reads from the
 *    loan-eval context. The visual builder writes this path into every
 *    `ConditionSpec` it produces; the runtime evaluator (conditions.ts
 *    `evaluate`) consumes the same path.
 *
 *  - `sourceUwmField` is the EXACT name from the UWM Insights guide so
 *    a reviewer can grep it. `null` when `source !== 'uwm'`.
 */

import type { ConditionOperator } from './conditions.js';
import { UWM_RAW_FIELD_CATALOG } from './uwm-catalog-generated.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Where the value originates. Drives the small "UWM | MANUAL" pill in the palette. */
export type FieldSource = 'uwm' | 'manual' | 'derived';

export type FieldType =
  | 'string'
  | 'number'
  | 'integer'
  | 'date'
  | 'datetime'
  | 'boolean_yn'
  | 'enum';

export type FieldCategory =
  | 'loan'
  | 'borrower'
  | 'parties'
  | 'dates'
  | 'status'
  | 'lock'
  | 'conditions'
  | 'docs'
  | 'closing';

export interface FieldEnumValue {
  value: string;
  label: string;
}

export interface LoanFieldDescriptor {
  /** Stable camelCase id used as the palette item key. */
  id: string;
  /** Dot-path consumed by ConditionSpec. Never null. */
  path: string;
  /** Exact UWM API field name, or null when source !== 'uwm'. */
  sourceUwmField: string | null;
  source: FieldSource;
  label: string;
  /** Human-friendly description shown as a tooltip in the palette. */
  description: string;
  type: FieldType;
  enumValues?: readonly FieldEnumValue[];
  allowedOps: readonly ConditionOperator[];
  /** Example value rendered in the placeholder for the value control. */
  exampleValue: string;
  category: FieldCategory;
  /** Required when source !== 'uwm'. Explains where the value comes from. */
  note?: string;
}

// ---------------------------------------------------------------------------
// Operator sets per type
// ---------------------------------------------------------------------------

const STRING_OPS: readonly ConditionOperator[] = ['eq', 'neq', 'exists', 'not_exists'];
const NUMERIC_OPS: readonly ConditionOperator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'exists',
  'not_exists',
];
const DATE_OPS: readonly ConditionOperator[] = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
  'exists',
  'not_exists',
];
const BOOLEAN_YN_OPS: readonly ConditionOperator[] = ['eq', 'neq', 'exists', 'not_exists'];
const ENUM_OPS: readonly ConditionOperator[] = ['eq', 'neq', 'exists', 'not_exists'];

/**
 * Return the operators that are valid for a given field type. The DnD
 * builder uses this to constrain the operator picker so the user can
 * never produce a `ConditionSpec` the schema rejects.
 */
export function operatorsForType(type: FieldType): readonly ConditionOperator[] {
  switch (type) {
    case 'string':
      return STRING_OPS;
    case 'number':
    case 'integer':
      return NUMERIC_OPS;
    case 'date':
    case 'datetime':
      return DATE_OPS;
    case 'boolean_yn':
      return BOOLEAN_YN_OPS;
    case 'enum':
      return ENUM_OPS;
  }
}

// ---------------------------------------------------------------------------
// Catalog
//
// Field path conventions (verified against MappedLoanCore in
// packages/shared/src/uwm/field-mapping.ts):
//
//   loan.<camelCase column>          — every UWM-sourced loan attribute
//   loan.address                     — derived; connector flattens
//                                      SubjectProperty.{Address1,City,State,Zip}
//                                      into the existing propertyAddress column
//   parties.<role>.fullName|email|phone   — manual contacts from LoanParty rows
// ---------------------------------------------------------------------------

/**
 * Hand-curated catalog of "core" loan fields the workflow builder palette
 * highlights. These get pretty labels + thoughtful descriptions and they
 * appear first in the palette so the most useful fields are always one
 * click away. The full UWM catalog (every documented Loans field) is
 * appended below via `UWM_RAW_FIELD_CATALOG`.
 */
const CURATED_LOAN_FIELDS: readonly LoanFieldDescriptor[] = [
  // --- Loan identity ------------------------------------------------------
  {
    id: 'loanNumber',
    path: 'loan.loanNumber',
    sourceUwmField: 'LoanId',
    source: 'uwm',
    label: 'Loan number',
    description: 'UWM loan number — the canonical id for this file.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '0012345678',
    category: 'loan',
  },
  {
    id: 'loanOfficer',
    path: 'loan.loanOfficer',
    sourceUwmField: 'LoanOfficer.FirstName + LastName',
    source: 'derived',
    label: 'Loan officer',
    description: "Assigned MLO's full name, composed from LoanOfficer.FirstName + LastName.",
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Jane Doe',
    category: 'loan',
    note: 'Derived from the nested LoanOfficer object on the UWM Loans query.',
  },

  // --- Borrowers ----------------------------------------------------------
  {
    id: 'borrower1FullName',
    path: 'loan.borrower1FullName',
    sourceUwmField: 'PrimaryBorrowerFirstName + PrimaryBorrowerLastName',
    source: 'derived',
    label: 'Borrower 1 full name',
    description: 'Primary borrower, composed from PrimaryBorrowerFirstName + LastName.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Pat Borrower',
    category: 'borrower',
  },
  {
    id: 'borrower2FullName',
    path: 'loan.borrower2FullName',
    sourceUwmField: 'SecondaryBorrower1FullName',
    source: 'uwm',
    label: 'Borrower 2 full name',
    description: 'Co-borrower #2 (UWM "SecondaryBorrower1FullName").',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Alex Borrower',
    category: 'borrower',
  },
  {
    id: 'borrower3FullName',
    path: 'loan.borrower3FullName',
    sourceUwmField: 'SecondaryBorrower2FullName',
    source: 'uwm',
    label: 'Borrower 3 full name',
    description: 'Co-borrower #3 (UWM "SecondaryBorrower2FullName").',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Sam Borrower',
    category: 'borrower',
  },
  {
    id: 'borrower4FullName',
    path: 'loan.borrower4FullName',
    sourceUwmField: 'SecondaryBorrower3FullName',
    source: 'uwm',
    label: 'Borrower 4 full name',
    description: 'Co-borrower #4 (UWM "SecondaryBorrower3FullName").',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Jordan Borrower',
    category: 'borrower',
  },
  {
    id: 'borrower1Phone',
    path: 'loan.borrower1Phone',
    sourceUwmField: 'BorrowerHomePhoneNumber',
    source: 'uwm',
    label: 'Borrower 1 phone',
    description: 'Primary borrower phone number from UWM (BorrowerHomePhoneNumber).',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '+15555550101',
    category: 'borrower',
  },
  {
    id: 'borrower1Email',
    path: 'loan.borrower1Email',
    sourceUwmField: 'BorrowerEmail',
    source: 'uwm',
    label: 'Borrower 1 email',
    description: 'Primary borrower email address from UWM (BorrowerEmail).',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'borrower@example.com',
    category: 'borrower',
  },
  {
    id: 'borrower2Phone',
    path: 'parties.borrower2.phone',
    sourceUwmField: null,
    source: 'manual',
    label: 'Borrower 2 phone',
    description: 'Phone for co-borrower #2. UWM does not expose phones for additional borrowers, so this must be entered on the Pipeline drawer.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '+15555550102',
    category: 'borrower',
    note: 'UWM Insights only returns BorrowerHomePhoneNumber for the primary borrower. Captured manually on the LoanParty row.',
  },
  {
    id: 'borrower2Email',
    path: 'parties.borrower2.email',
    sourceUwmField: null,
    source: 'manual',
    label: 'Borrower 2 email',
    description: 'Email for co-borrower #2. UWM does not expose emails for additional borrowers, so this must be entered on the Pipeline drawer.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'borrower2@example.com',
    category: 'borrower',
    note: 'UWM Insights only returns BorrowerEmail for the primary borrower. Captured manually on the LoanParty row.',
  },

  // --- Non-borrowing parties (all manual entry) ---------------------------
  {
    id: 'nonBorrowingSpouseFullName',
    path: 'parties.nonBorrowingSpouse.fullName',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing spouse — full name',
    description: 'Spouse who is on title/community-property paperwork but not on the loan.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Casey Spouse',
    category: 'parties',
    note: 'Manual entry on the Pipeline drawer; not exposed by UWM Insights.',
  },
  {
    id: 'nonBorrowingSpouseEmail',
    path: 'parties.nonBorrowingSpouse.email',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing spouse — email',
    description: 'Email address for the non-borrowing spouse.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'spouse@example.com',
    category: 'parties',
    note: 'Manual entry; not exposed by UWM Insights.',
  },
  {
    id: 'nonBorrowingSpousePhone',
    path: 'parties.nonBorrowingSpouse.phone',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing spouse — phone',
    description: 'Phone number for the non-borrowing spouse.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '+15555550103',
    category: 'parties',
    note: 'Manual entry; not exposed by UWM Insights.',
  },
  {
    id: 'nonBorrowingIndividualFullName',
    path: 'parties.nonBorrowingIndividual.fullName',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing individual — full name',
    description: 'A non-spouse individual on title or otherwise involved (e.g. trustee).',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Robin Trustee',
    category: 'parties',
    note: 'Manual entry; not exposed by UWM Insights.',
  },
  {
    id: 'nonBorrowingIndividualEmail',
    path: 'parties.nonBorrowingIndividual.email',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing individual — email',
    description: 'Email address for the non-borrowing individual.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'trustee@example.com',
    category: 'parties',
    note: 'Manual entry; not exposed by UWM Insights.',
  },
  {
    id: 'nonBorrowingIndividualPhone',
    path: 'parties.nonBorrowingIndividual.phone',
    sourceUwmField: null,
    source: 'manual',
    label: 'Non-borrowing individual — phone',
    description: 'Phone number for the non-borrowing individual.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '+15555550104',
    category: 'parties',
    note: 'Manual entry; not exposed by UWM Insights.',
  },

  // --- Property / address -------------------------------------------------
  {
    id: 'address',
    path: 'loan.propertyAddress',
    sourceUwmField: 'SubjectProperty.Address1 + City + State + Zip',
    source: 'derived',
    label: 'Property address',
    description: 'Subject property street, city, state, ZIP — composed from the nested SubjectProperty object.',
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: '123 Main St, Anytown MI 48000',
    category: 'loan',
    note: 'Flattened by the connector into the loan.propertyAddress column. SubjectProperty.Address1 is only valid inside that nested object on the UWM Loans query.',
  },

  // --- Status / lock ------------------------------------------------------
  {
    id: 'loanStatus',
    path: 'loan.loanStatus',
    sourceUwmField: 'LoanStatus.Description',
    source: 'uwm',
    label: 'Loan status',
    description: "UWM-reported loan stage description (e.g. 'File Imported', 'Underwriting').",
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Underwriting',
    category: 'status',
  },
  {
    id: 'lockStatus',
    path: 'loan.lockStatus',
    sourceUwmField: 'LockStatus',
    source: 'derived',
    label: 'Lock status',
    description: "Normalized lock state derived from LockStatus '0'/'1' — 'Locked', 'Not Locked', or 'Unknown'.",
    type: 'enum',
    enumValues: [
      { value: 'Locked', label: 'Locked' },
      { value: 'Not Locked', label: 'Not locked' },
      { value: 'Unknown', label: 'Unknown' },
    ],
    allowedOps: ENUM_OPS,
    exampleValue: 'Locked',
    category: 'lock',
  },
  {
    id: 'loanPurpose',
    path: 'loan.loanPurpose',
    sourceUwmField: 'Purpose + RefinancePurpose',
    source: 'derived',
    label: 'Loan purpose',
    description: "Purpose of the loan — 'Purchase', 'Refinance', or 'Refinance - <RefinancePurpose>'.",
    type: 'string',
    allowedOps: STRING_OPS,
    exampleValue: 'Purchase',
    category: 'loan',
    note: 'Normalized by the connector — UWM exposes Purpose=P/R plus a separate RefinancePurpose code.',
  },
  {
    id: 'lockExpirationDate',
    path: 'loan.lockExpirationDate',
    sourceUwmField: 'LockExpirationDate',
    source: 'uwm',
    label: 'Lock expiration date',
    description: 'Date the rate lock expires. Use before/after to drive expiry-warning workflows.',
    type: 'date',
    allowedOps: DATE_OPS,
    exampleValue: '2026-06-15',
    category: 'lock',
    note: 'UWM also exposes LockExpiration as a fallback; the connector prefers LockExpirationDate.',
  },

  // --- Disclosures + signatures -------------------------------------------
  {
    id: 'borr1LinkDelivered',
    path: 'loan.borr1LinkDelivered',
    sourceUwmField: 'Borr1LinkDelivered',
    source: 'uwm',
    label: 'Borrower 1 disclosure link delivered',
    description: 'Datetime UWM delivered the disclosure link to Borrower 1.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-05-01T14:00:00Z',
    category: 'docs',
  },
  {
    id: 'borr1SignedAndSubmitted',
    path: 'loan.borr1SignedAndSubmitted',
    sourceUwmField: 'Borr1SignedAndSubmitted',
    source: 'uwm',
    label: 'Borrower 1 signed & submitted',
    description: 'Datetime Borrower 1 finished signing the disclosures.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-05-02T17:30:00Z',
    category: 'docs',
  },
  {
    id: 'borr2LinkDelivered',
    path: 'loan.borr2LinkDelivered',
    sourceUwmField: 'Borr2LinkDelivered',
    source: 'uwm',
    label: 'Borrower 2 disclosure link delivered',
    description: 'Datetime UWM delivered the disclosure link to Borrower 2.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-05-01T14:00:00Z',
    category: 'docs',
  },
  {
    id: 'borr2SignedAndSubmitted',
    path: 'loan.borr2SignedAndSubmitted',
    sourceUwmField: 'Borr2SignedAndSubmitted',
    source: 'uwm',
    label: 'Borrower 2 signed & submitted',
    description: 'Datetime Borrower 2 finished signing the disclosures.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-05-02T17:30:00Z',
    category: 'docs',
  },
  {
    id: 'cdSignedFlag',
    path: 'loan.cdSignedFlag',
    sourceUwmField: 'CDSignedFlag',
    source: 'uwm',
    label: 'CD signed',
    description: "Closing Disclosure signed flag — 'Y' or 'N'.",
    type: 'boolean_yn',
    enumValues: [
      { value: 'Y', label: 'Yes' },
      { value: 'N', label: 'No' },
    ],
    allowedOps: BOOLEAN_YN_OPS,
    exampleValue: 'Y',
    category: 'docs',
  },
  {
    id: 'eSignCompletedFlag',
    path: 'loan.eSignCompletedFlag',
    sourceUwmField: 'ESignCompletedFlag',
    source: 'uwm',
    label: 'E-sign completed',
    description: "E-signature ceremony completed flag — 'Y' or 'N'.",
    type: 'boolean_yn',
    enumValues: [
      { value: 'Y', label: 'Yes' },
      { value: 'N', label: 'No' },
    ],
    allowedOps: BOOLEAN_YN_OPS,
    exampleValue: 'Y',
    category: 'docs',
  },

  // --- Conditions ---------------------------------------------------------
  {
    id: 'outstandingCondition',
    path: 'loan.outstandingCondition',
    sourceUwmField: 'OutstandingCondition',
    source: 'uwm',
    label: 'Outstanding conditions',
    description: 'Number of UW conditions still open on the loan. Resets to 0 when all are cleared.',
    type: 'integer',
    allowedOps: NUMERIC_OPS,
    exampleValue: '3',
    category: 'conditions',
  },

  // --- Sync / metadata ----------------------------------------------------
  {
    id: 'importedDateTime',
    path: 'loan.uwmImportedDateTime',
    sourceUwmField: 'ImportedDateTime',
    source: 'uwm',
    label: 'Imported date/time',
    description: 'Datetime the loan file was originally imported into UWM.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-04-10T12:00:00Z',
    category: 'dates',
  },

  // --- Closing dates ------------------------------------------------------
  {
    id: 'estimatedClosingDate',
    path: 'loan.estimatedClosingDate',
    sourceUwmField: 'EstimatedClosingDate',
    source: 'uwm',
    label: 'Estimated closing date',
    description: 'Best-known projected closing date.',
    type: 'date',
    allowedOps: DATE_OPS,
    exampleValue: '2026-07-15',
    category: 'closing',
  },
  {
    id: 'closingDateTime',
    path: 'loan.closingDateTime',
    sourceUwmField: 'ClosingDateTime',
    source: 'uwm',
    label: 'Closing date/time',
    description: 'Actual signing/closing appointment datetime.',
    type: 'datetime',
    allowedOps: DATE_OPS,
    exampleValue: '2026-07-15T15:00:00Z',
    category: 'closing',
  },
  {
    id: 'closingPackageReceivedDate',
    path: 'loan.closingPackageReceivedDate',
    sourceUwmField: 'ClosingPackageReceivedDate',
    source: 'uwm',
    label: 'Closing package received',
    description: 'Date the executed closing package was received back from settlement.',
    type: 'date',
    allowedOps: DATE_OPS,
    exampleValue: '2026-07-16',
    category: 'closing',
  },
  {
    id: 'disbursementDate',
    path: 'loan.disbursementDate',
    sourceUwmField: 'DisbursementDate',
    source: 'uwm',
    label: 'Disbursement date',
    description: 'Date funds were disbursed.',
    type: 'date',
    allowedOps: DATE_OPS,
    exampleValue: '2026-07-17',
    category: 'closing',
  },
  {
    id: 'epoDate',
    path: 'loan.epoDate',
    sourceUwmField: 'EPODate',
    source: 'uwm',
    label: 'EPO date',
    description: 'Early Payoff date — if set, the loan paid off inside the EPO window.',
    type: 'date',
    allowedOps: DATE_OPS,
    exampleValue: '2026-09-01',
    category: 'closing',
  },
] as const;

/**
 * The runtime-visible catalog: curated descriptors first, then every
 * auto-generated UWM catalog entry whose `sourceUwmField` is not already
 * exposed by a curated descriptor (we keep the curated label + path).
 *
 * "Pulled vs not pulled" is enforced at runtime by the worker — the
 * generated entries reference `uwm.<FieldName>` paths that resolve to
 * `undefined` when the field isn't in the active pull set. The palette
 * still shows them so authors can plan ahead and request a super-admin
 * opt-in via the UWM Fields admin page.
 */
function buildCatalog(): LoanFieldDescriptor[] {
  const out: LoanFieldDescriptor[] = [...CURATED_LOAN_FIELDS];
  // Build a Set of every UWM field already covered by the curated entries.
  // sourceUwmField can be composite like "A + B" — split and trim so a curated
  // derived field still suppresses the raw single-field auto entry.
  const curatedUwmFields = new Set<string>();
  for (const d of CURATED_LOAN_FIELDS) {
    if (!d.sourceUwmField) continue;
    for (const tok of d.sourceUwmField.split('+').map((s) => s.trim())) {
      if (/^[A-Z][A-Za-z0-9_.]*$/.test(tok)) curatedUwmFields.add(tok);
    }
  }
  for (const auto of UWM_RAW_FIELD_CATALOG) {
    // Skip auto entries that duplicate a curated descriptor's UWM field.
    if (auto.sourceUwmField && curatedUwmFields.has(auto.sourceUwmField)) continue;
    out.push(auto);
  }
  return out;
}

export const LOAN_FIELD_CATALOG: readonly LoanFieldDescriptor[] = buildCatalog();

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/**
 * Map of field id -> descriptor. Frozen so consumers can't mutate the
 * shared catalog at runtime.
 */
export const FIELD_CATALOG_BY_ID: Readonly<Record<string, LoanFieldDescriptor>> = Object.freeze(
  Object.fromEntries(LOAN_FIELD_CATALOG.map((f) => [f.id, f])),
);

/** Map of dot-path -> descriptor; used by the preview helpers. */
export const FIELD_CATALOG_BY_PATH: Readonly<Record<string, LoanFieldDescriptor>> = Object.freeze(
  Object.fromEntries(LOAN_FIELD_CATALOG.map((f) => [f.path, f])),
);

/** Stable-order list of categories present in the catalog (palette grouping). */
export const FIELD_CATEGORIES: readonly FieldCategory[] = [
  'loan',
  'borrower',
  'parties',
  'status',
  'lock',
  'conditions',
  'docs',
  'dates',
  'closing',
];

/** Look a descriptor up by id; undefined when the id is unknown. */
export function getFieldById(id: string): LoanFieldDescriptor | undefined {
  return FIELD_CATALOG_BY_ID[id];
}

/** Look a descriptor up by dot-path; undefined when the path is unknown. */
export function getFieldByPath(path: string): LoanFieldDescriptor | undefined {
  return FIELD_CATALOG_BY_PATH[path];
}
